import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';

import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { Store } from '@ngrx/store';
import { capitalize, compact } from 'lodash-es';
import {
    ILinearBetslipMessagesProviderService,
    LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
} from 'packages/sports/common/betslip/base/services/betslip-messages-provider.service';
import { IBetslipRootState } from 'packages/sports/common/betslip/base/store/state';
import { BetslipType } from 'packages/sports/common/betslip/core/betslip-type';
import { EpcotConfigService, EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';
import { combineLatest, take } from 'rxjs';

import { getLegsCount, isBetBuilderPick } from '../../../../../core/utils';
import { ISucceededSlipResult } from '../../../../betplacement/models';
import LinearBetslipMessagesService from '../../../../linear/linear-betslip-messages.service';
import { teaserBetPicksSelector, teaserBetSelectedPointsSelector } from '../../../../teaser-bet/selectors';
import { ITeaserBetPickState } from '../../../../teaser-bet/state';
import { BetslipResultActions } from '../../../actions';
import { IResultSucceededSlipSummaryViewModel } from '../../../models/result-succeeded-slip.model';
import { IBetslipResultState } from '../../../state';
import LinearResultBaseComponent from './linear-result-base.component';
import { DigitalResultSummaryService } from './linear-result-summary.service';

@Component({
    selector: 'bs-digital-linear-result-combo',
    templateUrl: './linear-result-combo.component.html',
    styleUrls: ['base-linear-result-model.component.scss', './linear-result-combo.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DigitalResultSummaryService],
    standalone: false,
})
export class LinearResultComboComponent extends LinearResultBaseComponent implements OnInit {
    @Input() succeededSlip: ISucceededSlipResult;
    @Input() betslipType = BetslipType.Combo;
    @Input() moduleName?: string;
    @Input() isTeaser = false;

    readonly isEpcotEnabled = this.epcotConfigService.isEnabled(EpcotModule.Betslip);
    readonly pickParlay: string;
    private readonly betbuilderComboHeaderSubTitle: string;
    private readonly systemBetHeaderSubTitle: string;
    readonly bets: string;
    private hasBetBuilderPick = false;
    summaryViewModel: IResultSucceededSlipSummaryViewModel;
    title: string;
    subTitle: string;
    totalOddsFormatted: string;
    id: string;
    selectedPoints: number;
    teaserPicksFromState: { [pickId: string]: ITeaserBetPickState };

    constructor(
        private resultSummaryService: DigitalResultSummaryService,
        @Inject(LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messagesProvider: ILinearBetslipMessagesProviderService,
        private epcotConfigService: EpcotConfigService,
        public store: Store<IBetslipRootState>,
        private changeRef: ChangeDetectorRef,
        private linearMessageService: LinearBetslipMessagesService,
    ) {
        super(store);

        this.messages = messagesProvider.getMessages();
        this.pickParlay = this.messages.linear.PickParlay;
        this.betbuilderComboHeaderSubTitle = this.messages.betslip.common.BetbuilderComboHeaderSubTitle;
        this.systemBetHeaderSubTitle = this.messages.betslip.common.SystemBetHeaderSubTitle;
        this.bets = this.messages.betslip.common.Bets;
    }

    ngOnInit(): void {
        combineLatest([this.store.select(teaserBetSelectedPointsSelector), this.store.select(teaserBetPicksSelector)])
            .pipe(take(1))
            .subscribe(([teasedPoints, teaserPicks]) => {
                this.teaserPicksFromState = teaserPicks;
                this.selectedPoints = teasedPoints;
                this.summaryViewModel = this.resultSummaryService.getSummaryViewModel(this.succeededSlip, this.betslipType);
                this.hasBetBuilderPick = this.succeededSlip.picks.some((pick) => !!pick.clientPick && isBetBuilderPick(pick.clientPick));
                this.title = this.getTitle();
                this.subTitle = this.getSubTitle();
                this.id = this.betslipType === BetslipType.System ? this.succeededSlip.systemKey! : BetslipType.Combo.toString();

                this.changeRef.detectChanges();
            });
    }

    private getTitle(): string {
        if (this.isTeaser) {
            return `${this.selectedPoints}-${this.messages.betslip.teaserBet?.messages.Point} ${this.messages.betslip.teaserBet?.messages.TeaserInfoTitle}`;
        }

        if (this.moduleName) {
            return this.moduleName;
        }

        if (this.isEpcotEnabled) {
            return this.hasBetBuilderPick ? this.messages.betslip.common.BetbuilderComboHeaderTitle : this.messages.linear.PickParlay;
        }

        if (this.hasBetBuilderPick) {
            return this.messages.linear.ParlaysHeader;
        }

        const pickCount = this.succeededSlip.picks.length;

        return this.linearMessageService.getComboSectionTitle(this.isEpcotEnabled, pickCount, this.hasBetBuilderPick, this.hasBetBuilderPick);
    }

    private getSubTitle(): string {
        if (this.isEpcotEnabled) {
            if (this.betslipType === BetslipType.System) {
                return interpolateDoubleBracesString(this.systemBetHeaderSubTitle, {
                    legsCount: getLegsCount(compact(this.succeededSlip.picks.map((p) => p.clientPick))),
                });
            }

            return interpolateDoubleBracesString(this.betbuilderComboHeaderSubTitle, {
                legsCount: getLegsCount(compact(this.succeededSlip.picks.map((p) => p.clientPick))),
            });
        }

        if (this.isTeaser || this.moduleName) {
            return '';
        }

        if (this.betslipType === BetslipType.System) {
            return `${this.succeededSlip.picks.length} ${capitalize(this.bets)}`;
        }

        if (!this.hasBetBuilderPick) {
            return '';
        }

        return interpolateDoubleBracesString(this.messages.linear.PicksCount, {
            count: getLegsCount(compact(this.succeededSlip.picks.map((p) => p.clientPick))),
        });
    }

    onKeepPlacedChanged(value: boolean): void {
        if (this.betslipType === BetslipType.System) {
            this.store.dispatch(
                BetslipResultActions.keepPlacedBetsInLinearSystem({ systemKey: this.succeededSlip.systemKey!, keepPlacedBetsInLinearSystem: value }),
            );

            return;
        }

        if (this.isTeaser) {
            this.store.dispatch(BetslipResultActions.keepPlacedBetsInLinearTeaser({ keepPlacedBetsInLinearTeaser: value }));
        } else {
            this.store.dispatch(BetslipResultActions.keepPlacedBetsInLinearCombo({ keepPlacedBetsInLinearCombo: value }));
        }
    }

    keepPlaced(resultState: IBetslipResultState): boolean {
        if (this.betslipType === BetslipType.System) {
            return resultState.keepPlacedBetsInLinearSystem[this.succeededSlip.systemKey!];
        }

        return this.isTeaser ? resultState.keepPlacedBetsInLinearTeaser : resultState.keepPlacedBetsInLinearCombo;
    }
}
